// ============================================================
// TERROR ENGINE — CHAT SYSTEM
// Zone-scoped chat. Every zone gets its own channel.
// Keyboard emojis supported natively — OS handles the picker.
//
// Features:
//   - Firebase path: zones/{zoneId}/chat/{msgId}
//   - Float rule: ≤3 chars OR emoji-only → floats above player head 3s
//   - Chat panel: slide-up from bottom, last 20 messages, auto-scroll
//   - Input: text field, Enter to send, any keyboard emoji works
//   - Auto-cleanup: messages older than 5min purged on load
//   - Multi-player: floaties render above any player who sent it
//
// Usage in any zone:
//   import { ChatSystem, useChatSystem } from "./ChatSystem";
//
//   const chat = useChatSystem({ zoneId: "plaza", uid, playerName, pos });
//
//   // In render:
//   <ChatSystem {...chat} players={players} tile={TILE} />
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { ref, push, onValue, remove, query, orderByChild, limitToLast } from "firebase/database";
import { db } from "./firebase";
import { SD } from "./theme";

// ── Emoji detection ───────────────────────────────────────────
// Matches emoji characters using Unicode ranges
const EMOJI_REGEX = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}){1,3}$/u;

function isFloatie(text) {
  const trimmed = text.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length <= 3) return true;       // short text always floats
  return EMOJI_REGEX.test(trimmed);           // emoji-only floats
}

// ── Auto-cleanup — messages older than 5 minutes ─────────────
const MSG_TTL = 5 * 60 * 1000;

// ─────────────────────────────────────────────────────────────
// useChatSystem — hook, drop into any zone's main component
// ─────────────────────────────────────────────────────────────
export function useChatSystem({ zoneId, uid, playerName, pos }) {
  const [messages, setMessages]   = useState([]);
  const [floaties, setFloaties]   = useState([]); // { id, uid, text, x, y }
  const [open, setOpen]           = useState(false);
  const [input, setInput]         = useState("");
  const floatTimers               = useRef({});

  // Session anchor — captured once on mount. Every client filters out
  // messages posted BEFORE they joined, so each login/refresh gives a
  // fresh empty feed. Messages still live in Firebase and other players
  // see them — this is purely a per-client display filter.
  const joinedAt                  = useRef(Date.now());

  // ── Listen to zone chat ───────────────────────────────────
  useEffect(() => {
    if (!zoneId || !uid) return;
    const chatRef = query(
      ref(db, `zones/${zoneId}/chat`),
      orderByChild("ts"),
      limitToLast(20)
    );
    const unsub = onValue(chatRef, snap => {
      if (!snap.exists()) { setMessages([]); return; }
      const now  = Date.now();
      const msgs = [];
      snap.forEach(child => {
        const m = { id: child.key, ...child.val() };
        // Purge server-side expired messages (TTL cleanup)
        if (now - m.ts > MSG_TTL) {
          remove(ref(db, `zones/${zoneId}/chat/${m.id}`));
          return;
        }
        // Client-side session filter — skip messages from before we joined
        if (m.ts < joinedAt.current) return;
        msgs.push(m);
      });
      setMessages(msgs);
      // Trigger floaties for new messages from others
      msgs.forEach(m => {
        if (m.uid === uid) return;           // don't float own messages
        if (floatTimers.current[m.id]) return; // already floating
        if (!isFloatie(m.text)) return;
        triggerFloat(m);
      });
    });
    return () => unsub();
  }, [zoneId, uid]);

  // ── Trigger a floating message ────────────────────────────
  const triggerFloat = useCallback((msg) => {
    const floatId = msg.id;
    setFloaties(prev => [...prev, { id: floatId, uid: msg.uid, text: msg.text, x: msg.x, y: msg.y, name: msg.name }]);
    floatTimers.current[floatId] = setTimeout(() => {
      setFloaties(prev => prev.filter(f => f.id !== floatId));
      delete floatTimers.current[floatId];
    }, 3000);
  }, []);

  // ── Send message ──────────────────────────────────────────
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || !uid || !playerName) return;
    setInput("");
    const msg = {
      uid, name: playerName,
      text, ts: Date.now(),
      x: pos?.x || 0, y: pos?.y || 0,
    };
    await push(ref(db, `zones/${zoneId}/chat`), msg);
    // Float own message too
    if (isFloatie(text)) {
      const tempId = `local_${Date.now()}`;
      triggerFloat({ id: tempId, ...msg });
    }
  }, [input, uid, playerName, pos, zoneId, triggerFloat]);

  const toggleOpen  = () => setOpen(o => !o);
  const closeChat   = () => setOpen(false);

  return { messages, floaties, open, input, setInput, sendMessage, toggleOpen, closeChat };
}

// ─────────────────────────────────────────────────────────────
// FloatingMessage — appears above a player's head
// ─────────────────────────────────────────────────────────────
function FloatingMessage({ text, x, y, tile, camOffset = { x: 0, y: 0 } }) {
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    // Fade in
    const fadeIn = setTimeout(() => setOpacity(1), 50);
    // Fade out near end
    const fadeOut = setTimeout(() => setOpacity(0), 2500);
    return () => { clearTimeout(fadeIn); clearTimeout(fadeOut); };
  }, []);

  const left = x * tile + tile / 2 - camOffset.x;
  const top  = y * tile - tile * 2 - camOffset.y;

  return (
    <div style={{
      position: "absolute",
      left, top,
      transform: "translateX(-50%)",
      zIndex: 80,
      pointerEvents: "none",
      opacity,
      transition: "opacity 0.4s ease",
      animation: "floatUp 3s ease-out forwards",
    }}>
      <div style={{
        background: "rgba(4,2,14,0.92)",
        border: `1px solid ${SD.purpleLight}44`,
        borderRadius: 20,
        padding: "5px 12px",
        fontSize: 16,
        whiteSpace: "nowrap",
        backdropFilter: "blur(4px)",
        boxShadow: `0 0 12px ${SD.purpleLight}22`,
        fontFamily: SD.fontDisplay,
        color: SD.starWhite,
      }}>
        {text}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChatPanel — slide-up message history + input
// ─────────────────────────────────────────────────────────────
function ChatPanel({ messages, input, setInput, sendMessage, onClose }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
    // Don't let chat input trigger movement
    e.stopPropagation();
  };

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      zIndex: 300,
      background: `linear-gradient(180deg, transparent, rgba(2,0,10,0.98) 20%)`,
      backdropFilter: "blur(8px)",
      borderTop: `1px solid ${SD.purpleLight}22`,
      fontFamily: SD.fontDisplay,
    }}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px 14px 4px",
      }}>
        <div style={{ fontSize: 9, color: SD.purpleLight, letterSpacing: 3 }}>◆ ZONE CHAT</div>
        <button onClick={onClose} style={{
          background: "transparent", border: "none",
          color: SD.starDim, cursor: "pointer", fontSize: 14,
          fontFamily: SD.fontDisplay, padding: "0 4px",
        }}>✕</button>
      </div>

      {/* Messages */}
      <div style={{
        maxHeight: 180, overflowY: "auto",
        padding: "4px 14px 8px",
        display: "flex", flexDirection: "column", gap: 4,
      }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 10, color: SD.starDim, fontStyle: "italic", textAlign: "center", padding: "12px 0" }}>
            No messages yet. Say something.
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{
            display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <span style={{
              fontSize: 9, color: SD.purpleLight, letterSpacing: 1,
              flexShrink: 0, paddingTop: 2, minWidth: 60,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{m.name}</span>
            <span style={{
              fontSize: 12, color: SD.starSilver, lineHeight: 1.5,
              wordBreak: "break-word",
            }}>{m.text}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        display: "flex", gap: 8, padding: "6px 12px 12px",
        alignItems: "center",
      }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Say something... or send an emoji 👋"
          autoFocus
          style={{
            flex: 1,
            background: `rgba(45,27,105,0.3)`,
            border: `1px solid ${SD.purpleLight}44`,
            borderRadius: 20,
            padding: "8px 16px",
            color: SD.starWhite,
            fontFamily: SD.fontDisplay,
            fontSize: 12,
            outline: "none",
          }}
        />
        <button onClick={sendMessage} style={{
          padding: "8px 16px",
          background: `linear-gradient(135deg, ${SD.purpleDim}, #3b0f6e)`,
          border: `1px solid ${SD.purpleLight}`,
          borderRadius: 20,
          color: SD.starWhite,
          fontFamily: SD.fontDisplay,
          fontSize: 11, fontWeight: 900,
          cursor: "pointer",
          letterSpacing: 1,
        }}>Send</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChatToggle — the chat bubble button, fixed bottom-left
// ─────────────────────────────────────────────────────────────
function ChatToggle({ onClick, unread, pos }) {
  // Position is prop-driven so an overseer (RoomOverlay) can stack the
  // bubble just above the MENU button. Defaults match the original
  // bottom-left placement so existing mounts (Plaza) are unchanged.
  // pos may specify any edges. Defaults keep the original bottom-left
  // placement so existing mounts are unchanged; a rail can pass
  // {top,right} instead to dock the bubble in a vertical chrome column.
  const hasV = pos && (pos.top != null || pos.bottom != null);
  const hasH = pos && (pos.left != null || pos.right != null);
  const posStyle = {
    top:    pos?.top,
    right:  pos?.right,
    bottom: hasV ? pos?.bottom : 20,
    left:   hasH ? pos?.left : 20,
  };
  return (
    <button onClick={onClick} style={{
      position: "fixed", ...posStyle, zIndex: 200,
      width: 44, height: 44, borderRadius: "50%",
      background: `linear-gradient(135deg, ${SD.purpleDim}, #3b0f6e)`,
      border: `1px solid ${SD.purpleLight}66`,
      color: SD.starWhite, fontSize: 18,
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer",
      boxShadow: unread ? `0 0 16px ${SD.purpleLight}88` : `0 0 8px ${SD.purpleLight}22`,
      transition: "box-shadow 0.3s",
    }}>
      💬
      {unread > 0 && (
        <div style={{
          position: "absolute", top: -4, right: -4,
          width: 16, height: 16, borderRadius: "50%",
          background: SD.purpleLight,
          fontSize: 9, fontWeight: 900,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: SD.void0, fontFamily: SD.fontDisplay,
        }}>{unread > 9 ? "9+" : unread}</div>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// ChatSystem — the full assembled system, drop into any zone
//
// Props from useChatSystem() + zone-specific:
//   players    { [uid]: { x, y, name } }  — for floatie positioning
//   tile       number                      — tile size
//   camOffset  { x, y }                   — camera offset (scrolling zones)
//   localUid   string                      — local player uid (skip own floaties on others)
// ─────────────────────────────────────────────────────────────
export function ChatSystem({
  messages, floaties, open, input, setInput,
  sendMessage, toggleOpen, closeChat,
  players = {}, tile = 48, camOffset = { x: 0, y: 0 },
  togglePos = null,
}) {
  // Count unread when panel is closed
  const [lastSeen, setLastSeen]   = useState(0);
  const [unread, setUnread]       = useState(0);

  useEffect(() => {
    if (open) {
      setLastSeen(messages.length);
      setUnread(0);
    } else {
      setUnread(Math.max(0, messages.length - lastSeen));
    }
  }, [messages.length, open]);

  return (
    <>
      {/* Floating messages above player heads */}
      {floaties.map(f => (
        <FloatingMessage
          key={f.id}
          text={f.text}
          x={f.x}
          y={f.y}
          tile={tile}
          camOffset={camOffset}
        />
      ))}

      {/* Chat panel */}
      {open && (
        <ChatPanel
          messages={messages}
          input={input}
          setInput={setInput}
          sendMessage={sendMessage}
          onClose={closeChat}
        />
      )}

      {/* Toggle button */}
      {!open && (
        <ChatToggle onClick={toggleOpen} unread={unread} pos={togglePos} />
      )}

      <style>{`
        @keyframes floatUp {
          0%   { transform: translateX(-50%) translateY(0px);   }
          100% { transform: translateX(-50%) translateY(-20px); }
        }
      `}</style>
    </>
  );
}